import { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useListDispatcherForwardsQuery } from '@shared/api';
import { Skeleton } from '@shared/ui/Skeleton';
import { ToneBadge, formatDt, type Tone } from './tone';

interface SentTabProps {
  /** Открыть карточку письма во вкладке «Входящие» (по id переписки). */
  onOpen: (correspondenceId: string) => void;
}

// Фильтр по «реальному» статусу отправки. Пустая строка — все три статуса
// (backend без `status` возвращает sent | manual | corrected).
type SentStatusFilter = '' | 'sent' | 'manual' | 'corrected';

const STATUS_META: Record<string, { label: string; tone: Tone }> = {
  sent:      { label: 'Отправлено',           tone: 'success' },
  manual:    { label: 'Отправлено вручную',    tone: 'success' },
  corrected: { label: 'Исправлено юристом',    tone: 'info' },
};

const FILTERS: ReadonlyArray<[SentStatusFilter, string]> = [
  ['', 'Все'],
  ['sent', 'Автоматически'],
  ['manual', 'Вручную'],
  ['corrected', 'Исправленные'],
];

export function SentTab({ onOpen }: SentTabProps) {
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<SentStatusFilter>('');

  // Debounce поиска — 280мс, минимум 2 символа (как во «Входящих»):
  // не дёргаем backend на каждый символ.
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed.length > 0 && trimmed.length < 2) {
      setSearchQuery('');
      return;
    }
    const t = window.setTimeout(() => setSearchQuery(trimmed), 280);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, isError, refetch } = useListDispatcherForwardsQuery({
    status: statusFilter || undefined,
    search: searchQuery || undefined,
    limit: 100,
    offset: 0,
  });

  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;

  return (
    <Panel>
      <Header>
        <div>
          <Title>Отправленные письма</Title>
          <Subtitle>
            Перенаправлено на ящики отделов: {total}
            {isLoading ? ' · загрузка…' : ''}
          </Subtitle>
        </div>
      </Header>

      <Controls>
        <SearchInput
          type="search"
          placeholder="Поиск: тема, отправитель или отдел…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <ChipRow>
          {FILTERS.map(([value, label]) => (
            <Chip
              key={value || 'all'}
              type="button"
              $active={statusFilter === value}
              onClick={() => setStatusFilter(value)}
            >
              {label}
            </Chip>
          ))}
        </ChipRow>
      </Controls>

      {isLoading ? (
        <List>
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonRow key={i}>
              <Skeleton $width="60%" $height={16} />
              <Skeleton $width="40%" $height={14} />
            </SkeletonRow>
          ))}
        </List>
      ) : isError ? (
        <Empty>
          <EmptyTitle>Не удалось загрузить отправленные письма</EmptyTitle>
          <RetryButton type="button" onClick={() => refetch()}>
            Повторить
          </RetryButton>
        </Empty>
      ) : items.length === 0 ? (
        <Empty>
          <EmptyTitle>Пока нет отправленных писем</EmptyTitle>
          <EmptyHint>
            Письма появятся здесь после того, как агент или юрист перенаправит
            их на ящик отдела.
          </EmptyHint>
        </Empty>
      ) : (
        <List>
          {items.map(({ correspondence, forward, route }) => {
            // status среди sent | manual | corrected — но backend теоретически
            // может вернуть и иной; STATUS_META — Record<string>, неизвестный
            // статус даёт undefined и бейдж просто не рисуется.
            const meta = STATUS_META[forward.status] ?? null;
            return (
              <Row
                key={forward.id}
                type="button"
                onClick={() => onOpen(correspondence.id)}
              >
                <RowMain>
                  <RowSubject>{correspondence.subject || '(без темы)'}</RowSubject>
                  <RowSender>{correspondence.sender || 'Отправитель не указан'}</RowSender>

                  {/* Ключевой элемент: КУДА отправлено письмо. */}
                  <Destination $known={route !== null}>
                    <Arrow aria-hidden="true">→</Arrow>
                    {route ? (
                      <>
                        <DestName>{route.display_name}</DestName>
                        <DestEmail>&lt;{route.email}&gt;</DestEmail>
                      </>
                    ) : (
                      <DestName>—</DestName>
                    )}
                  </Destination>
                </RowMain>

                <RowMeta>
                  {meta ? <ToneBadge $tone={meta.tone}>{meta.label}</ToneBadge> : null}
                  <MetaDate>
                    {formatDt(forward.sent_at)}
                    {forward.confidence != null
                      ? ` · AI ${Math.round(forward.confidence * 100)}%`
                      : ''}
                  </MetaDate>
                </RowMeta>
              </Row>
            );
          })}
        </List>
      )}
    </Panel>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  max-width: 920px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Subtitle = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  transition: border-color ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Chip = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.text.secondary};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primarySoft : theme.colors.surface};
  transition: border-color ${({ theme }) => theme.motion.fast}
      ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const rowIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Row = styled.button`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(4)};
  width: 100%;
  text-align: left;
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  cursor: pointer;
  animation: ${rowIn} ${({ theme }) => theme.motion.base}
    ${({ theme }) => theme.motion.easeOut};
  transition: border-color ${({ theme }) => theme.motion.fast}
      ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.fast}
      ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

const RowMain = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const RowSubject = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowSender = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Акцентный блок «куда отправлено». Зелёный фон/кант, когда маршрут известен;
// приглушённый — когда route === null.
const Destination = styled.div<{ $known: boolean }>`
  display: inline-flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ $known, theme }) =>
    $known ? `${theme.colors.status.success}1A` : theme.colors.surfaceHover};
  border: 1px solid
    ${({ $known, theme }) =>
      $known ? `${theme.colors.status.success}55` : theme.colors.border};
`;

const Arrow = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.status.success};
`;

const DestName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DestEmail = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  word-break: break-all;
`;

const RowMeta = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const MetaDate = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  white-space: nowrap;
`;

const SkeletonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(10)} ${({ theme }) => theme.spacing(4)};
  text-align: center;
`;

const EmptyTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EmptyHint = styled.div`
  max-width: 420px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const RetryButton = styled.button`
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  transition: background ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.backgroundElevated};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;
